import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { createBrowserRouter, createRoutesFromChildren, matchRoutes, RouteObject, useLocation, useNavigationType } from "react-router-dom";
import type { AppSettings } from "./AppSettings";
import { applyAuthenticationIdentity } from "./authenticationIdentity";

// A bootstrap instance attempts initialization once, before any router is wrapped.
export function createMonitoring() {
    let attempted = false;
    let initialized = false;

    function initialize(settings: typeof AppSettings): boolean {
        if (attempted) return initialized;
        attempted = true;
        if (!settings.sentry?.dsn) return false;
        try {
            const integrations = [
                // React Router V7 integration.
                // Helps with tracing URL changes and which URLs belong to the 
                // same route, for example:
                //    - https://test.com/form/1
                //    - https://test.com/form/2
                // both belong to the same route */form/*
                Sentry.reactRouterV7BrowserTracingIntegration({
                    useEffect: useEffect,
                    useLocation,
                    useNavigationType,
                    createRoutesFromChildren,
                    matchRoutes,
                }),
                // Replay integration
                // Records user actions and sends to Sentry.
                Sentry.replayIntegration({
                    // Whether to mask all text on the page when recording user actions
                    // When false only inputs are masked.
                    // There is a separate setting to force unmasked inputs.
                    maskAllText: false,
                    // Whether to mask all images on the page when recording user 
                    // actions.
                    blockAllMedia: false,
                    // Which URLs can the replay run on.
                    // URL must contain one of the strings in the array or match one of
                    // the regexes supplied
                    networkDetailAllowUrls: [window.location.origin],
                    // Capture the body of network requests
                    // Sentry will still run data scrubbing on the bodies.
                    networkCaptureBodies: true
                })
            ];

            if (settings.sentry.enableReportABug) {
                integrations.push(Sentry.feedbackIntegration({
                    // Disable the injection of the default widget
                    autoInject: false,
                    colorScheme: 'system',
                    showBranding: false,
                    showName: false,
                    showEmail: false,
                    tags: {
                        Application: settings.sentry.application,
                        Customer: settings.sentry.customer
                    },
                    useSentryUser: {
                        name: 'fullName',
                        email: 'email',
                    },
                }));
            }

            Sentry.init({
                // The DSN to use when sending information to Sentry.
                // Identifies a specific project in Sentry.
                dsn: settings.sentry.dsn,
                // Whether to send identifying information to Sentry such as
                // username, useragent, ip, etc
                sendDefaultPii: settings.sentry.sendDefaultPii,
                // Whether to sent available performance metrics to Sentry.
                // These are the basic performance metrics and not the endepth
                // profiling metrics that take a lot more effort to setup.
                enableMetrics: settings.sentry.enableMetrics,
                // Whether to attach stacktraces to actions sent to Sentry.
                attachStacktrace: settings.sentry.attachStacktrace,
                // Whether @sentry/react is in debug mode, logging more information
                // to the console.
                debug: settings.sentry.debug,
                // initialise integrations.
                integrations: integrations,
                // The environment to associate all action sent to Sentry with.
                environment: settings.sentry.environment,
                // Release to associate all actions sent to Sentry with.
                release: settings.sentry.release,
                // Percentage of captured traces that are sent to Sentry.
                // 1.0 = 100%, 0 = 0%
                tracesSampleRate: settings.sentry.tracesSampleRate,
                // Which URLs to trace / record in a trace.
                // Only trace internal URLs don't want to trace every SEO url
                // under the sun.
                tracePropagationTargets: [
                  'localhost', 
                  /^\/$/,
                  new RegExp(`/^${window.location.origin}`)
                ],
                // Percentage of captured session replays that are sent to Sentry.
                // 1.0 = 100%, 0 = 0%
                replaysSessionSampleRate: settings.sentry.replaysSessionSampleRate,
                // Percentage of captured session replays that are sent to Sentry.
                // when an error occurs
                // 1.0 = 100%, 0 = 0%
                replaysOnErrorSampleRate: settings.sentry.replaysOnErrorSampleRate,
                // Enable logs to be sent to Sentry.
                enableLogs: settings.sentry.enableLogs,
            });

            Sentry.setTag('Application', settings.sentry.application);
            Sentry.setTag('Customer', settings.sentry.customer);
            // Apply the current authentication identity after the client is initialized.
            applyAuthenticationIdentity();

            initialized = true;
        } catch (error) {
            console.error("[Sentry] Failed to initialize monitoring:", error);
        }
        return initialized;
    }

    function createRouter(routes: RouteObject[], settings: typeof AppSettings) {
        const create = initialize(settings)
            ? Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)
            : createBrowserRouter;
        return create(routes);
    }

    return { initialize, createRouter };
}

export const monitoring = createMonitoring();
